import classNames from "classnames";
import isEmpty from "lodash/fp/isEmpty";
import omit from "lodash/fp/omit";
import moment from "moment";
import React, { useRef, useState, useEffect, useContext } from "react";

import Button from "@rio-cloud/rio-uikit/Button";
import { AuthContext } from "src/providers/AuthProvider";
import ButtonDropdown from "@rio-cloud/rio-uikit/ButtonDropdown";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import SortDirection, {
  type SortDirectionType,
} from "@rio-cloud/rio-uikit/SortDirection";
import TableCardsSorting from "@rio-cloud/rio-uikit/TableCardsSorting";
import TableSearch from "@rio-cloud/rio-uikit/TableSearch";
import TableSettingsDialog from "@rio-cloud/rio-uikit/TableSettingsDialog";
import TableToolbar from "@rio-cloud/rio-uikit/TableToolbar";
import TableViewToggles, {
  type TableViewTogglesViewType,
} from "@rio-cloud/rio-uikit/TableViewToggles";
import PaginatorSize, { ConfigPaginator } from "../../PaginatorSize";
import Spinner from "@rio-cloud/rio-uikit/Spinner";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { IPackage, IPackageUser } from "@types";
import { formatDateBrUtc, formatDateUTCBr } from "src/utils";
import Select from "@rio-cloud/rio-uikit/Select";
import { fetchPackageUsers, createPackage, ICreatePackagePayload, fetchPackageById, updatePackageEtaDate, uploadBatchPackages } from "src/services/packages";
import { Notification, DatePicker } from "@rio-cloud/rio-uikit";
import SubNavigation from "@rio-cloud/rio-uikit/SubNavigation";
import { InputDate } from "../../index";
import "./Package.css";
import FilePicker from "@rio-cloud/rio-uikit/FilePicker";

type ModalMode = "create" | "view";

const defaultColumnOrder = [
  "name",
  "nf",
  "status",
  "shippingDate",
  "shippingEtaDate",
  "destinationLocation",
  "carrierName",
  "confirmDelivery",
  // "responsibleName",
  "receivedAt",
  "receivedBy"
];

type ColumnLabel = {
  [key: string]: string;
};
const columnLabels: ColumnLabel = {
  name: "Nome do Pacote",
  nf: "Nota Fiscal",
  status: "Status do envio",
  shippingDate: "Data de Envio",
  shippingEtaDate: "Previsão de Entrega",
  destinationLocation: "Destino",
   carrierName: "Transportadora",
  confirmDelivery: "Recebeu o pacote?",
  responsibleName: "Responsável",
  receivedAt: "Data de Recebimento",
  receivedBy: "Recebido por",
};

const columnIcons: ColumnLabel = {
  name: "rioglyph rioglyph-palette",
  nf: "rioglyph rioglyph-detail-view",
  shippingDate: "rioglyph rioglyph-calendar",
  shippingEtaDate: "rioglyph rioglyph-calendar",
  destinationLocation: "rioglyph rioglyph-finish",
  responsibleName: "rioglyph rioglyph-businessman",
  receivedAt: "rioglyph rioglyph-calendar",
  receivedBy: "rioglyph rioglyph-businessman",
};

type ColumnDetails = {
  [key: string]: number;
};

type ColumnDetailsMap = {
  [key: string]: ColumnDetails;
};

const demoColumnsDetails: ColumnDetailsMap = {
  name: {
    width: 200,
    defaultWidth: 200,
    maxWidth: 350,
  },
  origin: {
    width: 250,
    defaultWidth: 250,
    maxWidth: 350,
  },
  destination: {
    width: 150,
    defaultWidth: 200,
    maxWidth: 200,
  },
  stops: {
    width: 170,
    defaultWidth: 170,
    maxWidth: 200,
  },
  performance: {
    width: 278,
    defaultWidth: 278,
    maxWidth: 300,
  },
};

type PackageRow = IPackage & {
  shippingDate?: string;
  shippingEtaDate?: string;
  destinationLocation?: string;
};

const getSortDir = (
  currentDir: SortDirectionType,
  newSortBy: string,
  previousSortBy: string
): SortDirectionType => {
  if (newSortBy === previousSortBy) {

    return currentDir === SortDirection.ASCENDING
      ? SortDirection.DESCENDING
      : SortDirection.ASCENDING;
  }

  return SortDirection.ASCENDING;
};

export type Props = {
  viewType: TableViewTogglesViewType;
  packages: IPackage[];
  searchValue: string;
  totalElements: number;
  handleSearchValueChange: (text: string) => void;
  handleDownload: () => void;
  handleCreatePackage: () => void;
  handleUpdatePackage: (id: string) => void;
  handleDeletePackage: (id: string) => void;
  handleConfirmDelivery?: (id: string, status?: string) => void;
  handleMarkNotReceived?: (id: string) => void;
  handleLoadMore: (config: ConfigPaginator) => void;
  handleReset: () => void;
  handleSort: (dir: string, newSort: string) => void;
  showHeader?: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  onPackageCreated?: () => void;
  onDateFilterChange?: (start?: string, end?: string) => void;
  handleResetDate?: () => void;
};

const PackageTable = (props: Props) => {
  const { profileId } = useContext(AuthContext);
  const { viewType: externalViewType } = props;
  const sortBy = useRef("createdAt");
  const sortDir = useRef<SortDirectionType>(SortDirection.DESCENDING);

  const setSortBy = (value: string) => {
    sortBy.current = value;
  };

  const setSortDir = (value: SortDirectionType) => {
    sortDir.current = value;
  };

  const handleCreateRoute = () => {
    console.log("Erro ao implementar criação de rota");
  }

  const [showTableSettingsDialog, setShowTableSettingsDialog] = useState(false);
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnsDetails, setColumnsDetails] = useState(demoColumnsDetails);
  const [viewType, setViewType] = useState(
    externalViewType || TableViewToggles.VIEW_TYPE_TABLE
  );

  const [startDate, setStartDate] = useState<moment.Moment | undefined>(undefined);
  const [endDate, setEndDate] = useState<moment.Moment | undefined>(undefined);

  useEffect(() => {
    if (startDate && endDate) {
      if (startDate.isAfter(endDate)) {
        Notification.error("Data inicial não pode ser maior do que a data final.");
        return;
      }

      const formattedStart = startDate.clone().startOf("day").format("YYYY-MM-DD");
      const formattedEnd = endDate.clone().endOf("day").format("YYYY-MM-DD");
      props.onDateFilterChange?.(formattedStart, formattedEnd);
    }

    if (!startDate && !endDate) {
      props.onDateFilterChange?.(undefined, undefined);
    }
  }, [startDate, endDate]);

  const handleResetDates = () => {
    setStartDate(undefined);
    setEndDate(undefined);

    if (props?.handleResetDate) {
      props.handleResetDate();
    }
  };

  const inputPropsStart: React.HTMLProps<HTMLInputElement> = {
    placeholder: 'Data Inicial',
    id: 'package-date-start',
    style: { width: 140 }
  };

  const inputPropsEnd: React.HTMLProps<HTMLInputElement> = {
    placeholder: 'Data Final',
    id: 'package-date-end',
    style: { width: 140 }
  };

  if (!startDate) inputPropsStart.value = '';
  if (!endDate) inputPropsEnd.value = '';

  const handleToggleTableSettingsDialog = () =>
    setShowTableSettingsDialog(!showTableSettingsDialog);
  const handleViewTypeChange = (newViewType: TableViewTogglesViewType) =>
    setViewType(newViewType);

  const handleColumnChange = (
    newColumnOrder: string[],
    newHiddenColumns: string[],
    newColumnsDetails = columnsDetails
  ) => {
    setColumnOrder(newColumnOrder);
    setHiddenColumns(newHiddenColumns);
    setColumnsDetails(newColumnsDetails);
  };

  // For immediate effect
  const handleColumnDetailsChange = (
    column: string,
    newColumnDetails: ColumnDetails
  ) => {
    const updatedColumnsDetails = { ...columnsDetails };
    updatedColumnsDetails[column] = newColumnDetails;
    setColumnsDetails(updatedColumnsDetails);
  };

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const newSortBy = event.currentTarget.getAttribute("data-sortby");

    if (!newSortBy) return;

    const previousSortBy = sortBy.current;
    const previousSortDir = sortDir.current;

    const newSortDir = getSortDir(previousSortDir, newSortBy, previousSortBy);

    sortBy.current = newSortBy;
    sortDir.current = newSortDir;

    props.handleSort(newSortDir, newSortBy);
  };

  const handleCardSortChange = (
    newSortBy: string,
    newSortDir: SortDirectionType
  ) => {
    setSortBy(newSortBy);
    setSortDir(newSortDir);
    props.handleSort(newSortDir, newSortBy); // dispara na hora
  };

  // May be extracted as a dedicated component but for demo purpose it's shown here
  const renderTableHead = (
    column: string,
    label: string,
    sortByColumn: string,
    sortDirection: SortDirectionType
  ) => {
    const tableHeadClassNames = classNames("user-select-none", "sort-column");

    return (
      <th
        key={column}
        className={tableHeadClassNames}
        onClick={handleSortChange}
        data-field={column}
        data-sortby={column}
        title={label}
      >
        <span>
          {sortByColumn === column ? (
            <SortArrows direction={sortDirection} />
          ) : (
            <SortArrows />
          )}
          <span>{label}</span>
        </span>
      </th>
    );
  };

  const renderTableCaption = (column: string, columnDetails: ColumnDetails) => {
    const style = columnDetails?.width
      ? {
        minWidth: columnDetails.width,
        width: columnDetails.width,
      }
      : {};

    return <col key={column} style={style} />;
  };

  // filter for hidden columns
  const columns = (columnOrder || []).filter((name) => !(hiddenColumns || []).includes(name));

  // Transform packages to include flattened fields
  const transformedPackages = (props.packages || []).map((pkg) => ({
    ...pkg,
    shippingDate: pkg.shipping?.date
      ? formatDateUTCBr(pkg.shipping.date, true)
      : '--/--/----',
    shippingEtaDate: pkg.shipping?.etaDate
      ? formatDateUTCBr(pkg.shipping.etaDate, false)
      : '--/--/----',
    destinationLocation: pkg.destination?.dn || '-',
  })) as PackageRow[];

  // filter data to omit hidden columns
  const withoutHidden = omit(hiddenColumns);
  const filteredRows = transformedPackages.map((pkg) => ({
    ...withoutHidden(pkg),
  })) as PackageRow[];

  // in case a search value is given, filter the data accordingly
  const searchResult = filteredRows;

  // Sort rows according to the sortBy and sortDir settings
  const rows = searchResult;

  const tableClassNames = classNames(
    "table",
    "table-layout-fixed",
    "table-column-overflow-hidden",
    "table-bordered",
    "table-sticky",
    "table-head-filled",
    viewType === TableViewToggles.VIEW_TYPE_SINGLE_CARD &&
    "table-cards table-single-card",
    viewType === TableViewToggles.VIEW_TYPE_MULTI_CARDS &&
    "table-cards table-multi-cards"
  );

  const isViewTypeTable = viewType === TableViewToggles.VIEW_TYPE_TABLE;

  const cardSortingSelectOptions = (columns || []).map((column) => {
    return {
      id: column,
      label: columnLabels[column],
      selected: column === sortBy.current,
      disabled: false,
    };
  });

  const icons = {
    id: "",
    name: "rioglyph rioglyph-route-option margin-right-10 text-color-gray text-size-20",
    origin:
      "rioglyph rioglyph-start margin-right-10 text-color-gray text-size-16",
    destination:
      "rioglyph rioglyph-finish margin-right-10 text-color-gray text-size-16",
    stops:
      "rioglyph rioglyph-pushpin margin-right-10 text-color-gray text-size-16",
    distance:
      "rioglyph rioglyph-road margin-right-10 text-color-gray text-size-16",
    duration:
      "rioglyph rioglyph-time margin-right-10 text-color-gray text-size-16",
    register: "",
    vwcoin:
      "rioglyph rioglyph-circle-stack margin-right-10 text-color-gray text-size-16",
    performance: "",
  };

  const [packageModal, setPackageModal] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("create");
  const [activeTab, setActiveTab] = useState<string>("pacote-unico");
  const [notfisFile, setNotfisFile] = useState<File | null>(null);
  const [conembFile, setConembFile] = useState<File | null>(null);
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
  const [users, setUsers] = useState<IPackageUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isSavingEta, setIsSavingEta] = useState(false);

  // Form states
  const [packageName, setPackageName] = useState("");
  const [packageNf, setPackageNf] = useState("");
  const [shippingDate, setShippingDate] = useState<moment.Moment | undefined>(undefined);
  const [shippingTime, setShippingTime] = useState("");
  const [etaDate, setEtaDate] = useState<moment.Moment | undefined>(undefined);
  const [etaTime, setEtaTime] = useState("");
  const [destinationDn, setDestinationDn] = useState("");
  const [dealerName, setDealerName] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [location, setLocation] = useState("");
  const [carrierName, setCarrierName] = useState("");

  // Select states
  const [selectedDealerResponsible, setSelectedDealerResponsible] = useState<{ name: string; profileId: string } | null>(null);
  const [selectedResponsible, setSelectedResponsible] = useState<{ name: string; profileId: string } | null>(null);
  const [selectedSuperior1, setSelectedSuperior1] = useState<{ name: string; profileId: string } | null>(null);
  const [selectedSuperior2, setSelectedSuperior2] = useState<{ name: string; profileId: string } | null>(null);
  const [selectedSuperior3, setSelectedSuperior3] = useState<{ name: string; profileId: string } | null>(null);

  // View mode states for created/canceled/received by
  const [createdById, setCreatedById] = useState<string | null>(null);
  const [createdAt, setCreatedAt] = useState<string | null>(null);
  const [canceledById, setCanceledById] = useState<string | null>(null);
  const [canceledAt, setCanceledAt] = useState<string | null>(null);
  const [receivedByIdModal, setReceivedByIdModal] = useState<string | null>(null);
  const [receivedByNameModal, setReceivedByNameModal] = useState<string | null>(null);
  const [receivedAt, setReceivedAt] = useState<string | null>(null);
  const [packageStatus, setPackageStatus] = useState<string | null>(null);
  const [viewingPackageId, setViewingPackageId] = useState<string | null>(null);

  const isViewMode = modalMode === "view";

  const resetForm = () => {
    setPackageName("");
    setPackageNf("");
    setShippingDate(undefined);
    setShippingTime("");
    setEtaDate(undefined);
    setEtaTime("");
    setDestinationDn("");
    setDealerName("");
    setCnpj("");
    setLocation("");
    setCarrierName("");
    setSelectedDealerResponsible(null);
    setSelectedResponsible(null);
    setSelectedSuperior1(null);
    setSelectedSuperior2(null);
    setSelectedSuperior3(null);
    setCreatedById(null);
    setCreatedAt(null);
    setCanceledById(null);
    setCanceledAt(null);
    setReceivedByIdModal(null);
    setReceivedByNameModal(null);
    setReceivedAt(null);
    setPackageStatus(null);
    setViewingPackageId(null);
  };

  // Parse ISO datetime string (UTC) to separate date (moment) and time (string) em horário local
  // Ponto 16 - Tratar datas do backend como UTC e converter para local (evitar 3h a mais)
  const parseDateTimeString = (dateTimeStr: string | null | undefined): { date: moment.Moment | undefined; time: string } => {
    if (!dateTimeStr) return { date: undefined, time: "" };
    const trimmed = dateTimeStr.slice(0, 23) + (dateTimeStr.includes("Z") || dateTimeStr.includes("+") ? "" : "Z");
    const parsed = moment.utc(trimmed).local();
    if (!parsed.isValid()) return { date: undefined, time: "" };
    return {
      date: parsed,
      time: parsed.format("HH:mm"),
    };
  };

  // Extract UUID from receivedById (remove prefix like "prod-rio-users:")
  const extractUUID = (receivedById: string | null | undefined): string => {
    if (!receivedById) return "";
    const parts = receivedById.split(":");
    return parts.length > 1 ? parts[1] : receivedById;
  };

  // Find user name by profileId
  const findUserNameByProfileId = (profileId: string | null, usersList: IPackageUser[]): string => {
    if (!profileId) return "";
    const user = usersList.find((u) => u.profileId === profileId);
    return user?.name || "";
  };

  const handleOpenCreateModal = () => {
    setModalMode("create");
    resetForm();
    setActiveTab("pacote-unico");
    setNotfisFile(null);
    setConembFile(null);
    setPackageModal(true);
  };

  const handleOpenViewModal = async (packageId: string) => {
    setModalMode("view");
    resetForm();
    setPackageModal(true);
    setViewingPackageId(packageId);
    setIsLoadingDetails(true);

    try {
      // Fetch users first to get names for superiors
      const usersResponse = await fetchPackageUsers();
      const usersList = usersResponse.data.result;
      setUsers(usersList);

      // Fetch package details
      const response = await fetchPackageById(packageId);
      const pkg = response.data.result;

      // Populate form fields
      setPackageName(pkg.name || "");
      setPackageNf(pkg.nf || "");

      // Parse shipping dates
      const shippingDateTime = parseDateTimeString(pkg.shipping?.date);
      setShippingDate(shippingDateTime.date);
      setShippingTime(shippingDateTime.time);

      const etaDateTime = parseDateTimeString(pkg.shipping?.etaDate);
      setEtaDate(etaDateTime.date);
      setEtaTime(etaDateTime.time);

      // Set destination
      setDestinationDn(pkg.destination?.dn || "");
      setDealerName(pkg.destination?.dealerName || "");
      setCnpj(pkg.destination?.cnpj || "");
      setLocation(pkg.destination?.location || "");
      setCarrierName(pkg.carrierName || "");

      // Set dealer responsible
      if (pkg.dealerResponsibleProfileId) {
        const dealerResponsibleName = pkg.dealerResponsibleName || findUserNameByProfileId(pkg.dealerResponsibleProfileId, usersList);
        setSelectedDealerResponsible({
          name: dealerResponsibleName,
          profileId: pkg.dealerResponsibleProfileId,
        });
      }

      // Set responsible
      setSelectedResponsible({
        name: pkg.responsibleName || "",
        profileId: pkg.responsibleProfileId || "",
      });

      // Set superiors with names from users list
      if (pkg.superior1ProfileId) {
        const superior1Name = findUserNameByProfileId(pkg.superior1ProfileId, usersList);
        setSelectedSuperior1({ name: superior1Name, profileId: pkg.superior1ProfileId });
      }

      if (pkg.superior2ProfileId) {
        const superior2Name = findUserNameByProfileId(pkg.superior2ProfileId, usersList);
        setSelectedSuperior2({ name: superior2Name, profileId: pkg.superior2ProfileId });
      }

      if (pkg.superior3ProfileId) {
        const superior3Name = findUserNameByProfileId(pkg.superior3ProfileId, usersList);
        setSelectedSuperior3({ name: superior3Name, profileId: pkg.superior3ProfileId });
      }

      // Set created/canceled/received by info
      setCreatedById(pkg.createdById || null);
      setCreatedAt(pkg.createdAt || null);
      setCanceledById(pkg.canceledById || null);
      setCanceledAt(pkg.canceledAt || null);
      setReceivedByIdModal(pkg.receivedById || null);
      setReceivedByNameModal(pkg.receivedByName || null);
      setReceivedAt(pkg.receivedAt || null);
      setPackageStatus(pkg.status || null);
    } catch (error) {
      console.error("Erro ao buscar detalhes do pacote:", error);
      Notification.error("Não foi possível carregar os detalhes do pacote.");
      setPackageModal(false);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleClosePackageModal = () => {
    setPackageModal(false);
    resetForm();
  };

  // Fetch users on component mount for receivedBy column
  useEffect(() => {
    const loadUsersOnMount = async () => {
      try {
        const response = await fetchPackageUsers();
        setUsers(response.data.result);
      } catch (error) {
        console.error("Erro ao buscar usuários:", error);
      }
    };
    loadUsersOnMount();
  }, []);

  // Fetch users when modal opens in create mode
  useEffect(() => {
    if (packageModal && modalMode === "create") {
      const loadUsers = async () => {
        setUsersLoading(true);
        try {
          const response = await fetchPackageUsers();
          setUsers(response.data.result);
        } catch (error) {
          console.error("Erro ao buscar usuários:", error);
        } finally {
          setUsersLoading(false);
        }
      };
      loadUsers();
    }
  }, [packageModal, modalMode]);

  // Remove duplicate users by profileId and map to Select options format
  const uniqueUsers = users.filter(
    (user, index, self) => index === self.findIndex((u) => u.profileId === user.profileId)
  );

  const userOptions = uniqueUsers.map((user) => ({
    id: user.profileId,
    label: user.name,
    selected: false,
  }));

  const handleDealerResponsibleChange = (selectedItem: any) => {
    if (selectedItem) {
      const user = uniqueUsers.find((u) => u.profileId === selectedItem.id);
      if (user) {
        setSelectedDealerResponsible({ name: user.name, profileId: user.profileId });
      }
    } else {
      setSelectedDealerResponsible(null);
    }
  };

  const handleResponsibleChange = (selectedItem: any) => {
    if (selectedItem) {
      const user = uniqueUsers.find((u) => u.profileId === selectedItem.id);
      if (user) {
        setSelectedResponsible({ name: user.name, profileId: user.profileId });
      }
    } else {
      setSelectedResponsible(null);
    }
  };

  const handleSuperior1Change = (selectedItem: any) => {
    if (selectedItem) {
      const user = uniqueUsers.find((u) => u.profileId === selectedItem.id);
      if (user) {
        setSelectedSuperior1({ name: user.name, profileId: user.profileId });
      }
    } else {
      setSelectedSuperior1(null);
    }
  };

  const handleSuperior2Change = (selectedItem: any) => {
    if (selectedItem) {
      const user = uniqueUsers.find((u) => u.profileId === selectedItem.id);
      if (user) {
        setSelectedSuperior2({ name: user.name, profileId: user.profileId });
      }
    } else {
      setSelectedSuperior2(null);
    }
  };

  const handleSuperior3Change = (selectedItem: any) => {
    if (selectedItem) {
      const user = uniqueUsers.find((u) => u.profileId === selectedItem.id);
      if (user) {
        setSelectedSuperior3({ name: user.name, profileId: user.profileId });
      }
    } else {
      setSelectedSuperior3(null);
    }
  };

  const combineDateAndTime = (date: moment.Moment | undefined, time: string): string => {
    if (!date) return "";
    const timeValue = time || "00:00";
    const m = moment(`${date.format("YYYY-MM-DD")}T${timeValue}`, "YYYY-MM-DDTHH:mm");
    return m.format();
  };

  const handleSubmitPackage = async () => {
    // Validation
    if (!packageName || !packageNf || !selectedResponsible) {
      Notification.error("Preencha os campos obrigatórios: Nome do pacote, Nota fiscal e Responsável");
      return;
    }

    if (!shippingDate || !etaDate) {
      Notification.error("Preencha as datas de envio e previsão de entrega");
      return;
    }

    setIsSubmitting(true);

    const payload: ICreatePackagePayload = {
      name: packageName,
      nf: packageNf,
      createdById: profileId,
      responsible: {
        name: selectedResponsible.name,
        profileId: selectedResponsible.profileId,
      },
      dealerResponsible: selectedDealerResponsible ? {
        name: selectedDealerResponsible.name,
        profileId: selectedDealerResponsible.profileId,
      } : null,
      superior1: selectedSuperior1 ? {
        name: selectedSuperior1.name,
        profileId: selectedSuperior1.profileId,
      } : null,
      superior2: selectedSuperior2 ? {
        name: selectedSuperior2.name,
        profileId: selectedSuperior2.profileId,
      } : null,
      superior3: selectedSuperior3 ? {
        name: selectedSuperior3.name,
        profileId: selectedSuperior3.profileId,
      } : null,
      status: "PENDING",
      packageReceived: null,
      canceledAt: null,
      shipping: {
        date: combineDateAndTime(shippingDate, shippingTime),
        etaDate: combineDateAndTime(etaDate, etaTime),
      },
      destination: {
        dn: destinationDn,
        dealerName: dealerName,
        cnpj: cnpj,
        location: location,
      },
      carrierName: carrierName || "",
    };

    console.log("Payload de envio para o backend:", payload);

    try {
      await createPackage(payload);
      Notification.success("Pacote criado com sucesso!");
      handleClosePackageModal();
      props.onPackageCreated?.();
    } catch (error) {
      console.error("Erro ao criar pacote:", error);
      Notification.error("Não foi possível criar o pacote.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePickNotfisFile = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setNotfisFile(fileList[0]);
  };

  const handlePickConembFile = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setConembFile(fileList[0]);
  };

  const handleSubmitBatch = async () => {
    if (!notfisFile || !conembFile) {
      Notification.error("Anexe os dois arquivos: NOTFIS e CONEMB.");
      return;
    }

    setIsSubmittingBatch(true);
    try {
      await uploadBatchPackages(notfisFile, conembFile);
      Notification.success("Pacotes criados com sucesso. Acompanhe o status na lista.");
      handleClosePackageModal();
      props.onPackageCreated?.();
    } catch (error) {
      console.error("Erro ao enviar arquivos em lote:", error);
      Notification.error("Não foi possível enviar os arquivos.");
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  return (
    <div id="TableCommonDemo">
      {props.showHeader ? (
        <>
          <Dialog
                    show={packageModal}
                    onClose={handleClosePackageModal}
                    onEsc={handleClosePackageModal}
                    bsSize="md"
                    bodyClassName="padding-20"
                    title={isViewMode ? "Detalhes do pacote" : "Criar pacote"}
                    body={
                      isLoadingDetails ? (
                        <div className="display-flex justify-content-center align-items-center padding-50">
                          <Spinner />
                        </div>
                      ) : isViewMode ? (
                      <div className="display-flex flex-column gap-16">
                        {/* Seção 1 - Informações do pacote (View Mode - sem abas) */}
                        <div className="display-flex flex-row flex-wrap gap-10">
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageName">Nome do pacote</label>
                            <input
                              id="packageName"
                              className="form-control"
                              type="text"
                              placeholder="Ex: Amortecedores"
                              value={packageName}
                              onChange={(e) => setPackageName(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageNf">Nota fiscal</label>
                            <input
                              id="packageNf"
                              className="form-control"
                              type="text"
                              placeholder="Ex: 9328476"
                              value={packageNf}
                              onChange={(e) => setPackageNf(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageShippingDate">Data de envio</label>
                            {isViewMode ? (
                              <input
                                id="packageShippingDate"
                                className="form-control"
                                type="text"
                                value={shippingDate ? shippingDate.format("DD/MM/YYYY") : ""}
                                readOnly
                              />
                            ) : (
                              <DatePicker
                                className="width-100pct padding-left-0"
                                inputProps={{
                                  placeholder: "Selecione a data",
                                  id: "packageShippingDate",
                                  className: "form-control",
                                }}
                                dateFormat="DD/MM/YYYY"
                                timeFormat={false}
                                locale="pt-BR"
                                value={shippingDate}
                                onChange={(value: moment.Moment | string, isValid: boolean) => {
                                  if (!value || value === "") {
                                    setShippingDate(undefined);
                                  } else if (isValid && moment.isMoment(value)) {
                                    setShippingDate(value);
                                  }
                                }}
                              />
                            )}
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageShippingTime">Horário de envio</label>
                            <input
                              id="packageShippingTime"
                              className="form-control"
                              type="time"
                              value={shippingTime}
                              onChange={(e) => setShippingTime(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageEtaDate">Previsão de entrega</label>
                            {isViewMode && packageStatus !== "DELAYED" ? (
                              <input
                                id="packageEtaDate"
                                className="form-control"
                                type="text"
                                value={etaDate ? etaDate.format("DD/MM/YYYY") : ""}
                                readOnly
                              />
                            ) : (
                              <DatePicker
                                className="width-100pct padding-left-0"
                                inputProps={{
                                  placeholder: "Selecione a data",
                                  id: "packageEtaDate",
                                  className: "form-control",
                                }}
                                dateFormat="DD/MM/YYYY"
                                timeFormat={false}
                                locale="pt-BR"
                                value={etaDate}
                                onChange={(value: moment.Moment | string, isValid: boolean) => {
                                  if (!value || value === "") {
                                    setEtaDate(undefined);
                                  } else if (isValid && moment.isMoment(value)) {
                                    setEtaDate(value);
                                  }
                                }}
                              />
                            )}
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageEtaTime">Previsão de entrega (Horário)</label>
                            <input
                              id="packageEtaTime"
                              className="form-control"
                              type="time"
                              value={etaTime}
                              onChange={(e) => setEtaTime(e.target.value)}
                              readOnly={isViewMode && packageStatus !== "DELAYED"}
                            />
                          </div>
                        </div>

                        {/* Separador 1 */}
                        <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />

                        {/* Seção 2 - Destino */}
                        <div className="display-flex flex-row flex-wrap gap-10">
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageDestinationDn">DN de destino</label>
                            <input
                              id="packageDestinationDn"
                              className="form-control"
                              type="text"
                              placeholder="Ex: 2000"
                              value={destinationDn}
                              onChange={(e) => setDestinationDn(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageDealerName">Nome da concessionária</label>
                            <input
                              id="packageDealerName"
                              className="form-control"
                              type="text"
                              placeholder="Ex: VWCO Curitiba - Unidade Industrial"
                              value={dealerName}
                              onChange={(e) => setDealerName(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageCnpj">CNPJ</label>
                            <input
                              id="packageCnpj"
                              className="form-control"
                              type="text"
                              placeholder="Ex: 12.345.678/0001-90"
                              value={cnpj}
                              onChange={(e) => setCnpj(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageLocation">Local</label>
                            <input
                              id="packageLocation"
                              className="form-control"
                              type="text"
                              placeholder="Ex: BR-277, Km 102, Curitiba - PR"
                              value={location}
                              onChange={(e) => setLocation(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                        </div>

                        {/* Separador 2 */}
                        <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />

                        {/* Seção 3 - Responsáveis */}
                        <div className="display-flex flex-row flex-wrap gap-10">
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageResponsible">Responsável pelo recebimento</label>
                            {isViewMode ? (
                              <input
                                id="packageResponsible"
                                className="form-control"
                                type="text"
                                value={selectedResponsible?.name || ""}
                                readOnly
                              />
                            ) : (
                              <Select
                                id="packageResponsible"
                                placeholder={usersLoading ? "Carregando..." : "Selecione um responsável"}
                                options={userOptions}
                                useFilter
                                noItemMessage="Responsável não encontrado"
                                onChange={handleResponsibleChange}
                              />
                            )}
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageSuperior1">1º superior</label>
                            {isViewMode ? (
                              <input
                                id="packageSuperior1"
                                className="form-control"
                                type="text"
                                value={selectedSuperior1?.name || ""}
                                readOnly
                              />
                            ) : (
                              <Select
                                id="packageSuperior1"
                                placeholder={usersLoading ? "Carregando..." : "Selecione um responsável"}
                                options={userOptions}
                                useFilter
                                noItemMessage="Responsável não encontrado"
                                onChange={handleSuperior1Change}
                              />
                            )}
                          </div>
                        </div>

                        {/* Separador 3 */}
                        <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />

                        {/* Seção - Responsável pela transportadora */}
                        <div className="display-flex flex-row flex-wrap gap-10">
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageCarrierName">Nome da transportadora</label>
                            <input
                              id="packageCarrierName"
                              className="form-control"
                              type="text"
                              placeholder="Ex: Braspress"
                              value={carrierName}
                              onChange={(e) => setCarrierName(e.target.value)}
                              readOnly={isViewMode}
                            />
                          </div>
                          <div style={{ width: 'calc(50% - 5px)' }}>
                            <label htmlFor="packageDealerResponsible">Responsável pela transportadora</label>
                            {isViewMode ? (
                              <input
                                id="packageDealerResponsible"
                                className="form-control"
                                type="text"
                                value={selectedDealerResponsible?.name || ""}
                                readOnly
                              />
                            ) : (
                              <Select
                                id="packageDealerResponsible"
                                placeholder={usersLoading ? "Carregando..." : "Selecione um responsável"}
                                options={userOptions}
                                useFilter
                                noItemMessage="Responsável não encontrado"
                                onChange={handleDealerResponsibleChange}
                              />
                            )}
                          </div>
                        </div>

                        {/* Seção 4 - Informações de criação/recebimento/cancelamento (apenas no modo view) */}
                        {isViewMode && (createdById || canceledById || receivedByIdModal) && (
                          <>
                            <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />
                            <div className="display-flex flex-column gap-10">
                              {createdById && (
                                <div className="display-flex align-items-center gap-5">
                                  <span className="rioglyph rioglyph-user text-color-primary" />
                                  <span className="text-size-14 text-color-primary">
                                    Pacote criado por: <strong>{findUserNameByProfileId(extractUUID(createdById), users) || '-'}</strong>
                                    {createdAt && <> em <strong>{formatDateBrUtc(createdAt)}</strong></>}
                                  </span>
                                </div>
                              )}
                              {packageStatus === "RECEIVED" && (receivedByIdModal || receivedByNameModal) && (
                                <div className="display-flex align-items-center gap-5">
                                  <span className="rioglyph rioglyph-user text-color-success" />
                                  <span className="text-size-14 text-color-success">
                                    Pacote recebido por: <strong>{receivedByNameModal || (receivedByIdModal ? findUserNameByProfileId(extractUUID(receivedByIdModal), users) : null) || '-'}</strong>
                                    {receivedAt && <> em <strong>{formatDateBrUtc(receivedAt)}</strong></>}
                                  </span>
                                </div>
                              )}
                              {packageStatus === "CANCELLED" && canceledById && (
                                <div className="display-flex align-items-center gap-5">
                                  <span className="rioglyph rioglyph-user text-color-danger" />
                                  <span className="text-size-14 text-color-danger">
                                    Pacote cancelado por: <strong>{findUserNameByProfileId(extractUUID(canceledById), users) || '-'}</strong>
                                    {canceledAt && <> em <strong>{formatDateBrUtc(canceledAt)}</strong></>}
                                  </span>
                                </div>
                              )}
                            </div>
                          </>
                        )}

                        {/* Botão de confirmar recebimento (apenas no modo view e status PENDING) */}
                        {isViewMode && packageStatus === "PENDING" && viewingPackageId && (
                          <>
                            <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />
                            <div className="display-flex justify-content-center">
                              <Button
                                bsStyle={Button.PRIMARY}
                                onClick={() => {
                                  if (viewingPackageId && props.handleConfirmDelivery) {
                                    props.handleConfirmDelivery(viewingPackageId, packageStatus || undefined);
                                    handleClosePackageModal();
                                  }
                                }}
                              >
                                Confirmar recebimento
                              </Button>
                            </div>
                          </>
                        )}

                        {/* Botões para status DELAYED - Confirmar recebimento e Salvar nova previsão */}
                        {isViewMode && packageStatus === "DELAYED" && viewingPackageId && (
                          <>
                            <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />
                            <div className="display-flex justify-content-center gap-10">
                              <Button
                                bsStyle={Button.PRIMARY}
                                onClick={() => {
                                  if (viewingPackageId && props.handleConfirmDelivery) {
                                    props.handleConfirmDelivery(viewingPackageId, packageStatus || undefined);
                                    handleClosePackageModal();
                                  }
                                }}
                              >
                                Confirmar recebimento
                              </Button>
                              <Button
                                bsStyle={Button.DEFAULT}
                                disabled={isSavingEta || !etaDate}
                                onClick={async () => {
                                  if (!viewingPackageId || !etaDate) return;

                                  setIsSavingEta(true);
                                  try {
                                    const newEtaDateTime = combineDateAndTime(etaDate, etaTime);
                                    await updatePackageEtaDate(viewingPackageId, newEtaDateTime);
                                    Notification.success("Nova previsão de entrega salva com sucesso!");
                                    handleClosePackageModal();
                                    props.onPackageCreated?.();
                                  } catch (error) {
                                    console.error("Erro ao salvar nova previsão:", error);
                                    Notification.error("Não foi possível salvar a nova previsão de entrega.");
                                  } finally {
                                    setIsSavingEta(false);
                                  }
                                }}
                              >
                                {isSavingEta ? "Salvando..." : "Salvar nova previsão"}
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                      ) : (
                      <div>
                        <div className="bg-white border-bottom-only border-width-1 border-style-solid border-color-light package-tabs-full-width">
                          <SubNavigation
                            className="padding-0 margin-0"
                            navItems={[
                              {
                                key: "pacote-unico",
                                route: (
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setActiveTab("pacote-unico");
                                    }}
                                    className={activeTab === "pacote-unico" ? "active" : ""}
                                  >
                                    PACOTE ÚNICO
                                  </a>
                                ),
                              },
                              {
                                key: "pacote-em-lote",
                                route: (
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setActiveTab("pacote-em-lote");
                                    }}
                                    className={activeTab === "pacote-em-lote" ? "active" : ""}
                                  >
                                    PACOTE EM LOTE
                                  </a>
                                ),
                              },
                            ]}
                          />
                        </div>

                        {activeTab === "pacote-unico" && (
                          <div className="display-flex flex-column gap-16 padding-top-15">
                            {/* Seção 1 - Informações do pacote */}
                            <div className="display-flex flex-row flex-wrap gap-10">
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageName">Nome do pacote</label>
                                <input
                                  id="packageName"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: Amortecedores"
                                  value={packageName}
                                  onChange={(e) => setPackageName(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageNf">Nota fiscal</label>
                                <input
                                  id="packageNf"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: 9328476"
                                  value={packageNf}
                                  onChange={(e) => setPackageNf(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageShippingDate">Data de envio</label>
                                <DatePicker
                                  className="width-100pct padding-left-0"
                                  inputProps={{
                                    placeholder: "Selecione a data",
                                    id: "packageShippingDate",
                                    className: "form-control",
                                  }}
                                  dateFormat="DD/MM/YYYY"
                                  timeFormat={false}
                                  locale="pt-BR"
                                  value={shippingDate}
                                  onChange={(value: moment.Moment | string, isValid: boolean) => {
                                    if (!value || value === "") {
                                      setShippingDate(undefined);
                                    } else if (isValid && moment.isMoment(value)) {
                                      setShippingDate(value);
                                    }
                                  }}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageShippingTime">Horário de envio</label>
                                <input
                                  id="packageShippingTime"
                                  className="form-control"
                                  type="time"
                                  value={shippingTime}
                                  onChange={(e) => setShippingTime(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageEtaDate">Previsão de entrega</label>
                                <DatePicker
                                  className="width-100pct padding-left-0"
                                  inputProps={{
                                    placeholder: "Selecione a data",
                                    id: "packageEtaDate",
                                    className: "form-control",
                                  }}
                                  dateFormat="DD/MM/YYYY"
                                  timeFormat={false}
                                  locale="pt-BR"
                                  value={etaDate}
                                  onChange={(value: moment.Moment | string, isValid: boolean) => {
                                    if (!value || value === "") {
                                      setEtaDate(undefined);
                                    } else if (isValid && moment.isMoment(value)) {
                                      setEtaDate(value);
                                    }
                                  }}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageEtaTime">Previsão de entrega (Horário)</label>
                                <input
                                  id="packageEtaTime"
                                  className="form-control"
                                  type="time"
                                  value={etaTime}
                                  onChange={(e) => setEtaTime(e.target.value)}
                                />
                              </div>
                            </div>

                            {/* Separador 1 */}
                            <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />

                            {/* Seção 2 - Destino */}
                            <div className="display-flex flex-row flex-wrap gap-10">
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageDestinationDn">DN de destino</label>
                                <input
                                  id="packageDestinationDn"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: 2000"
                                  value={destinationDn}
                                  onChange={(e) => setDestinationDn(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageDealerName">Nome da concessionária</label>
                                <input
                                  id="packageDealerName"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: VWCO Curitiba - Unidade Industrial"
                                  value={dealerName}
                                  onChange={(e) => setDealerName(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageCnpj">CNPJ</label>
                                <input
                                  id="packageCnpj"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: 12.345.678/0001-90"
                                  value={cnpj}
                                  onChange={(e) => setCnpj(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageLocation">Local</label>
                                <input
                                  id="packageLocation"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: BR-277, Km 102, Curitiba - PR"
                                  value={location}
                                  onChange={(e) => setLocation(e.target.value)}
                                />
                              </div>
                            </div>

                            {/* Separador 2 */}
                            <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />

                            {/* Seção 3 - Responsáveis */}
                            <div className="display-flex flex-row flex-wrap gap-10">
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageResponsible">Responsável pelo recebimento</label>
                                <Select
                                  id="packageResponsible"
                                  placeholder={usersLoading ? "Carregando..." : "Selecione um responsável"}
                                  options={userOptions}
                                  useFilter
                                  noItemMessage="Responsável não encontrado"
                                  onChange={handleResponsibleChange}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageSuperior1">1º superior</label>
                                <Select
                                  id="packageSuperior1"
                                  placeholder={usersLoading ? "Carregando..." : "Selecione um responsável"}
                                  options={userOptions}
                                  useFilter
                                  noItemMessage="Responsável não encontrado"
                                  onChange={handleSuperior1Change}
                                />
                              </div>
                            </div>

                            {/* Separador 3 */}
                            <div className="height-1 bg-light margin-y-15" style={{ width: '100%' }} />

                            {/* Seção - Responsável pela transportadora */}
                            <div className="display-flex flex-row flex-wrap gap-10">
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageCarrierName">Nome da transportadora</label>
                                <input
                                  id="packageCarrierName"
                                  className="form-control"
                                  type="text"
                                  placeholder="Ex: Braspress"
                                  value={carrierName}
                                  onChange={(e) => setCarrierName(e.target.value)}
                                />
                              </div>
                              <div style={{ width: 'calc(50% - 5px)' }}>
                                <label htmlFor="packageDealerResponsible">Responsável pela transportadora</label>
                                <Select
                                  id="packageDealerResponsible"
                                  placeholder={usersLoading ? "Carregando..." : "Selecione um responsável"}
                                  options={userOptions}
                                  useFilter
                                  noItemMessage="Responsável não encontrado"
                                  onChange={handleDealerResponsibleChange}
                                />
                              </div>
                            </div>
                          </div>
                        )}

                        {activeTab === "pacote-em-lote" && (
                          <div className="display-flex flex-column align-items-center justify-content-center padding-25 gap-10">
                            <div className="display-flex flex-row gap-15">
                              <div className="display-flex flex-column align-items-center gap-5">
                                <FilePicker
                                  displayMode="button"
                                  label={
                                    <span>
                                      <span className="rioglyph rioglyph-upload margin-right-5" />
                                      Anexar NOTFIS
                                    </span>
                                  }
                                  multiple={false}
                                  onPick={handlePickNotfisFile}
                                  accept={{ "text/plain": [".txt"] }}
                                />
                                {notfisFile && (
                                  <span className="text-size-12 text-color-dark">{notfisFile.name}</span>
                                )}
                              </div>
                              <div className="display-flex flex-column align-items-center gap-5">
                                <FilePicker
                                  displayMode="button"
                                  label={
                                    <span>
                                      <span className="rioglyph rioglyph-upload margin-right-5" />
                                      Anexar CONEMB
                                    </span>
                                  }
                                  multiple={false}
                                  onPick={handlePickConembFile}
                                  accept={{ "text/plain": [".txt"] }}
                                />
                                {conembFile && (
                                  <span className="text-size-12 text-color-dark">{conembFile.name}</span>
                                )}
                              </div>
                            </div>
                            <span
                              className="text-color-gray text-center margin-top-10"
                              style={{
                                fontFamily: 'Source Sans Pro, sans-serif',
                                fontWeight: 400,
                                fontStyle: 'normal',
                                fontSize: 16,
                                lineHeight: '150%',
                                letterSpacing: '0%',
                                verticalAlign: 'middle',
                              }}
                            >
                              Anexe os arquivos CONEMB e NOTFIS para criar múltiplos pacotes de forma simultânea.
                            </span>
                            <div className="display-flex border rounded padding-15 margin-top-25 bg-highlight-decent">
                              <p className="rioglyph rioglyph-exclamation-sign text-color-primary text-size-18 margin-right-15"/>
                              <div className="display-flex flex-column">
                                <p className="text-bold text-size-16 text-dark">Importante</p>
                                <p className="text-size-14 margin-top-0">
                                  Os arquivos CONEMB e NOTFIS serão aceitos exclusivamente no formato .txt, com tamanho máximo de 10 MB.
                                  Após anexar os arquivos, os pacotes serão inseridos automaticamente na tela de Pacotes.
                                </p>
                              </div>

                            </div>

                          </div>
                        )}
                      </div>
                      )
                    }
                    footer={
                      !isViewMode ? (
                        <div className="display-flex justify-content-end">
                          {activeTab === "pacote-unico" ? (
                            <Button
                              bsStyle={Button.PRIMARY}
                              onClick={handleSubmitPackage}
                              disabled={isSubmitting || !packageName || !selectedResponsible}
                            >
                              {isSubmitting ? "Criando..." : "Criar pacote"}
                            </Button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={!notfisFile || !conembFile || isSubmittingBatch}
                              onClick={handleSubmitBatch}
                              style={{
                                height: 34,
                                borderRadius: 4,
                                border: '1px solid',
                                paddingLeft: 12,
                                paddingRight: 12,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              {isSubmittingBatch ? "Criando..." : "Criar pacotes"}
                            </button>
                          )}
                        </div>
                      ) : null
                    }
                  />
          <TableToolbar>
            <div className="table-toolbar-container">
              <div className="table-toolbar-group-left">
                <div className="table-toolbar-column margin-bottom-0 width-100pct">
                  <div className="btn-toolbar table-btn-toolbar">
                    <Button
                      bsStyle={Button.PRIMARY}
                      iconName="rioglyph-plus"
                      onClick={handleOpenCreateModal}
                    >
                      Criar Pacote
                    </Button>
                    <div className="width-400">
                      <TableSearch
                        className="width-100pct"
                        value={props.searchValue}
                        onChange={props.handleSearchValueChange}
                        placeholder="Buscar em pacotes por Nome, Nota Fiscal, Responsável, Transportadora"
                      />
                    </div>
                    <div className="gap-10 display-flex" style={{ marginTop: -20 }}>
                      <InputDate
                        label=""
                        inputProps={inputPropsStart}
                        dateFormat="DD/MM/YYYY"
                        timeFormat={false}
                        value={startDate}
                        onChange={(value, isValid) => {
                          if (!value || value === "") {
                            setStartDate(undefined);
                          } else if (isValid && moment.isMoment(value)) {
                            setStartDate(value);
                          }
                        }}
                      />
                      <InputDate
                        label=""
                        inputProps={inputPropsEnd}
                        dateFormat="DD/MM/YYYY"
                        timeFormat={false}
                        value={endDate || ''}
                        onChange={(value, isValid) => {
                          if (!value || value === "") {
                            setEndDate(undefined);
                          } else if (isValid && moment.isMoment(value)) {
                            setEndDate(value);
                          }
                        }}
                      />
                      <Button
                        onClick={handleResetDates}
                        bsStyle={Button.PRIMARY}
                        style={{ marginTop: 20 }}
                        className="rioglyph rioglyph-trash"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="table-toolbar-group-right">
                {/* <div className="table-toolbar-column table-toolbar-column-spacer">
                  <button
                    type="button"
                    className="btn btn-default btn-icon-only"
                    onClick={props.handleDownload}
                  >
                    <span
                      className="rioglyph rioglyph-download"
                      aria-hidden="true"
                    ></span>
                  </button>
                </div> */}
                <div className="table-toolbar-column">
                  
                  <TableViewToggles
                    initialViewType={viewType}
                    onViewTypeChange={handleViewTypeChange}
                    tableViewTooltipContent="Table view"
                    singleCardViewTooltipContent="List view"
                    multiCardsViewTooltipContent="Cards view"
                  />
                  
                </div>
              </div>
              {showTableSettingsDialog && (
                <TableSettingsDialog
                  show={showTableSettingsDialog}
                  title="Table settings"
                  onHide={handleToggleTableSettingsDialog}
                  onColumnChange={handleColumnChange}
                  defaultColumnOrder={defaultColumnOrder}
                  defaultHiddenColumns={[]}
                  columnOrder={columnOrder}
                  hiddenColumns={hiddenColumns}
                  columnLabels={columnLabels}
                  disabledColumns={[]}
                  closeButtonText="Close"
                  resetButtonText="Reset to default"
                  searchPlaceholder="Search by column name"
                  notFoundMessage="No column found for this search value"
                  columnsDetails={columnsDetails}
                  autoLabel="Auto"
                  onColumnDetailsChange={handleColumnDetailsChange}
                  onSearchChange={(val: string) => console.log(val)}
                  immediateChange
                />
              )}
            </div>
          </TableToolbar>
          {sortBy && !isViewTypeTable && (
            <TableCardsSorting
              selectOptions={cardSortingSelectOptions}
              sortName={sortBy.current}
              sortOrder={sortDir.current}
              onSortChange={handleCardSortChange}
            />
          )}
        </>
      ) : null}

      <div>
        {!props.isLoading && isEmpty(rows) && !isViewTypeTable && (
          <NotFoundState
            headline="Sem resultados"
            message="Por favor refaça a busca."
          />
        )}

        <table className={tableClassNames}>
          <colgroup>
            {(columns || []).map((column) =>
              renderTableCaption(column, columnsDetails[column])
            )}
            <col className="table-action" />
          </colgroup>
          <thead>
            <tr>
              {(columns || []).map((column) =>
                renderTableHead(column, columnLabels[column], sortBy.current, sortDir.current)
              )}
              <th className="table-action" />
            </tr>
          </thead>
          <tbody>
            {props.isLoading ? (
              <tr>
                <td colSpan={columns.length + 1}>
                  <div className="display-flex justify-content-center align-items-center padding-50">
                    <Spinner />
                  </div>
                </td>
              </tr>
            ) : null}

            {!props.isLoading && isEmpty(rows) && isViewTypeTable && (
              <tr>
                <td colSpan={columns.length + 1}>
                  <NotFoundState
                    outerClassName="border-none"
                    headline="Sem resultados"
                    message="Por favor refaça a busca."
                  />
                </td>
              </tr>
            )}
            {!props.isLoading && (rows || []).map((row: PackageRow, index: number) => {
              const getStatusColor = (status: string) => {
                switch (status) {
                  case "RECEIVED":
                    return "#43BE2B";
                  case "PENDING":
                    return "#A1A8B2";
                  case "CANCELLED":
                    return "#DB2B2B";
                  case "TO_BE_CONFIRMED":
                    return "#F5A623";
                  case "DELAYED":
                    return "#DB2B2B";
                  default:
                    return "#888";
                }
              };

              const getStatusLabel = (status: string) => {
                switch (status) {
                  case "RECEIVED":
                    return "Entregue";
                  case "PENDING":
                    return "Em rota";
                  case "CANCELLED":
                    return "Cancelado";
                  case "IN_TRANSIT":
                    return "Em Trânsito";
                  case "TO_BE_CONFIRMED":
                    return "A confirmar";
                  case "DELAYED":
                    return "Atrasado";
                  default:
                    return status;
                }
              };

              const renderCellValue = (col: string, value: any): React.ReactNode => {
                if (value === null || value === undefined || value === '') {
                  return '-';
                }

                // Se for objeto, não renderizar
                if (typeof value === 'object') {
                  return '-';
                }

                return String(value);
              };

              return (
                <tr key={row.id || index}>
                  {(columns || []).map((col) => (
                    <td
                      key={col}
                      data-field={columnLabels[col]}
                      className={col === "status" ? "package-status-cell" : undefined}
                    >
                      {col === "status" ? (
                        <span className="package-status-badge-wrapper" style={{ display: "inline-block", width: "fit-content", maxWidth: "100%", flex: "0 0 auto" }}>
                          <span
                            style={{
                              background: getStatusColor(row.status),
                              borderRadius: 20,
                              overflow: "hidden",
                              color: "white",
                              paddingLeft: 16,
                              paddingRight: 16,
                              paddingTop: 4,
                              paddingBottom: 4,
                              display: "inline-block",
                            }}
                          >
                            {getStatusLabel(row.status)}
                          </span>
                        </span>
                      ) : col === "shippingDate" ? (
                        <span>
                          {columnIcons[col] && (
                            <span className={`${columnIcons[col]} margin-right-5 text-color-gray`} />
                          )}
                          {row.shipping?.date ? formatDateUTCBr(row.shipping.date, true) : '--/--/----'}
                        </span>
                      ) : col === "shippingEtaDate" ? (
                        <span>
                          {columnIcons[col] && (
                            <span className={`${columnIcons[col]} margin-right-5 text-color-gray`} />
                          )}
                          {row.shipping?.etaDate ? formatDateUTCBr(row.shipping.etaDate, true) : '--/--/----'}
                        </span>
                      ) : col === "receivedAt" ? (
                        <span>
                          {columnIcons[col] && (
                            <span className={`${columnIcons[col]} margin-right-5 text-color-gray`} />
                          )}
                          {row.receivedAt ? formatDateBrUtc(row.receivedAt) : '--/--/----'}
                        </span>
                      ) : col === "receivedBy" ? (
                        <span>
                          {columnIcons[col] && (
                            <span className={`${columnIcons[col]} margin-right-5 text-color-gray`} />
                          )}
                          {row.receivedByName || (row.receivedById ? findUserNameByProfileId(extractUUID(row.receivedById), users) : null) || '-'}
                        </span>
                      ) : col === "confirmDelivery" ? (
                        <div>
                          {row.status === "RECEIVED" ? (
                            <span className="text-color-success text-bold">Confirmado</span>
                          ) : row.status === "CANCELLED" ? (
                            <span className="text-color-danger text-bold">Cancelado</span>
                          ) : (
                            (() => {
                              const etaDateValue = row.shipping?.etaDate ? moment.utc((row.shipping.etaDate.slice(0, 19) + "Z")) : null;
                              const isToBeConfirmed = row.status === "TO_BE_CONFIRMED";
                              const isOverdue = etaDateValue && moment().isAfter(etaDateValue);
                              const canClickNo = isToBeConfirmed && isOverdue;
                              return (
                                <div className="display-flex gap-5">
                                  <Button
                                    className="bg-success text-color-white"
                                    bsSize="sm"
                                    onClick={() => props.handleConfirmDelivery?.(row.id, row.status)}
                                  >
                                    SIM
                                  </Button>
                                  <Button
                                    className="bg-danger text-color-white"
                                    bsSize="sm"
                                    disabled={!canClickNo}
                                    onClick={() => canClickNo && props.handleMarkNotReceived?.(row.id)}
                                  >
                                    NÃO
                                  </Button>
                                </div>
                              );
                            })()
                          )}
                        </div>
                      ) : (
                        <span>
                          {columnIcons[col] && (
                            <span className={`${columnIcons[col]} margin-right-5 text-color-gray`} />
                          )}
                          {renderCellValue(col, row[col as keyof PackageRow])}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="table-action">
                    <span>
                      <ButtonDropdown
                        bsSize="md"
                        dropdownClassName="width-200"
                        title={
                          <span className="rioglyph rioglyph-option-vertical" />
                        }
                        variant="link"
                        iconOnly
                        items={[
                          {
                            value: (
                              <div
                                onClick={() => handleOpenViewModal(row.id)}
                              >
                                <span className="rioglyph rioglyph-clipboard margin-right-10 text-color-primary" />
                                <span>Ver detalhes do pacote</span>
                              </div>
                            ),
                          },
                          {
                            value: (
                              <div
                                onClick={() => row.status === "PENDING" && props.handleDeletePackage(row.id)}
                                style={{
                                  opacity: row.status === "PENDING" ? 1 : 0.5,
                                  cursor: row.status === "PENDING" ? "pointer" : "not-allowed",
                                }}
                              >
                                <span className={`rioglyph rioglyph-ban-circle margin-right-10 ${row.status === "PENDING" ? "text-color-primary" : "text-color-gray"}`} />
                                <span>Cancelar pacote</span>
                              </div>
                            ),
                          },
                        ]}
                      />
                    </span>
                  </td>
                </tr>
              );
            })}
            {/* Placeholder workaround for equal with cards of the last row */}
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
            <tr className="table-card-placeholder" />
          </tbody>
        </table>
      </div>
      
      {!props.isLoading ? (
        <PaginatorSize
          size={props.packages.length}
          totalSize={props.totalElements}
          isLoading={props.isLoadingMore}
          handleLoadMore={props.handleLoadMore}
          handleReset={props.handleReset}
        />
      ) : null}
    </div>
  );
};

export default PackageTable;
// fix/respostas_mensagens_pacotes_amity: pontos 11 e 12